import {
  type CustomRulesetParams,
  useRulesets
} from '@bo-hooks/rulesets/useRulesets';
import {
  LeagueTree,
  LineupLimits,
  Metadata,
  PlayerSelectionRules,
  StateManagement
} from '@bo-components/rulesets';
import { useEffect, useMemo } from 'react';
import { Loading } from '@library/components/Loading';
import { MessageBanner } from '@library/components/banners/MessageBanner';
import { PageTitle } from '@library/components/PageTitle';
import { PreviewConfirmationModal } from '@library/components/rulesets/PreviewConfirmationModal';
import { PrimaryButton } from '@library/components/buttons/PrimaryButton';
import { PrizepicksSwitch } from '@library/components/switches/PrizepicksSwitch';
import { RulesetsContext } from '@bo-hooks/rulesets/useRulesetsContext';
import { SecondaryButton } from '@library/components/buttons';
import { Spinner } from '@library/components/Spinner';
import { useSearchParams } from 'react-router-dom';
import { useSentrySetDashBoardTag } from '@bo-hooks/sentry/useSentrySetDashBoardTag';
import { withProfiler } from '@sentry/react';

export const RulesetsDashboardView = () => {
  useSentrySetDashBoardTag({ dashboardName: 'Rulesets' });

  const [searchParams] = useSearchParams();
  const memberGroupId = searchParams.get('memberGroupId');
  const memberId = searchParams.get('memberId');

  const custom = useMemo<CustomRulesetParams | undefined>(() => {
    if (memberGroupId) {
      return {
        hierarchyType: 'UserGroup',
        rootLevelScopeIds: [memberGroupId]
      };
    }

    if (memberId) {
      return {
        hierarchyType: 'User',
        rootLevelScopeIds: [memberId]
      };
    }

    return undefined;
  }, [memberGroupId, memberId]);

  const { rulesetsContext } = useRulesets({
    custom
  });

  const {
    selectedQualifiedPath,
    setSelectedQualifiedPath,
    gameOpsGlobalRuleset,
    gameModesGlobalRulesets,
    currentRuleset,
    expandAllPhantoms,
    setExpandAllPhantoms,
    modifiedFields,
    handleSubmitUpdate,
    isSubmitSuccess,
    isSubmitError,
    isSubmitLoading,
    resetUpdate,
    resetCreate,
    isRulesetFetching,
    isGameOpsLoading,
    isGameOpsFetching,
    isGameModesLoading,
    isGameModesFetching,
    handleDiscardChanges,
    handleResetRuleset,
    handleOpenPreviewModal,
    handleClosePreviewModal,
    isPreviewModalOpen,
    handlePreviewChanges,
    isPreviewLoading,
    previewData,
    customRuleset,
    isFetchingCustomRulesetRuleset
  } = rulesetsContext;

  const isLoading =
    isGameOpsLoading || isGameModesLoading || isFetchingCustomRulesetRuleset;

  useEffect(() => {
    if (selectedQualifiedPath) return;

    if (customRuleset?.attributes.qualified_path) {
      setSelectedQualifiedPath(customRuleset.attributes.qualified_path);
    } else if (gameOpsGlobalRuleset?.attributes.qualified_path) {
      setSelectedQualifiedPath(gameOpsGlobalRuleset?.attributes.qualified_path);
    }
  }, [
    gameOpsGlobalRuleset,
    customRuleset,
    selectedQualifiedPath,
    customRuleset?.attributes.qualified_path,
    gameOpsGlobalRuleset?.attributes.qualified_path,
    setSelectedQualifiedPath
  ]);

  let selectionSubtitle;
  if (currentRuleset) {
    if (currentRuleset.id) {
      selectionSubtitle = currentRuleset.attributes.name
        ? `(Selected: "${currentRuleset.attributes.name}" - ID: ${currentRuleset.id})`
        : '';
    } else {
      selectionSubtitle = `(Selected: "${currentRuleset.attributes.name}")`;
    }
  }

  const handleSelectRuleset = (rulesetPath: string) => {
    setSelectedQualifiedPath(rulesetPath);
  };

  const closeMessageBanner = () => {
    resetUpdate();
    resetCreate();
  };

  const handleStartPreview = async () => {
    if (!currentRuleset) return;

    if (!currentRuleset.id) {
      // Phantoms do not have any children and thus have no down stream effects.
      await handleSubmitUpdate();
    } else {
      handleOpenPreviewModal();
      await handlePreviewChanges();
    }
  };

  const handleConfirmSubmitChanges = async () => {
    await handleSubmitUpdate();
    handleClosePreviewModal();
  };

  return (
    <RulesetsContext.Provider value={rulesetsContext}>
      <div className="flex w-auto flex-col gap-4 pb-2 pr-6 pt-3">
        <div className="flex flex-row justify-start gap-1.5 pl-6">
          <PageTitle title="Rulesets" className="w-fit flex-initial" />
          {!!selectionSubtitle && (
            <span className="text-soFresh-140 flex self-end pb-2 text-xs">
              {selectionSubtitle}
            </span>
          )}
          <div className="grow" />
          {(isSubmitLoading ||
            isPreviewLoading ||
            isGameOpsFetching ||
            isGameModesFetching) && <Spinner className="mr-1 mt-2.5" />}

          <div className="flex items-center gap-2 self-center">
            <label
              htmlFor="toggle-expand-all-phantoms"
              className="text-soClean-160 rounded-md text-xs leading-6"
            >
              Show Potential Rulesets
            </label>
            <PrizepicksSwitch
              id="toggle-expand-all-phantoms"
              active={expandAllPhantoms}
              handleChange={setExpandAllPhantoms}
            />
          </div>

          <SecondaryButton
            title="Reset"
            handleOnClick={handleResetRuleset}
            className="px-2 py-0 text-sm"
            disabled={isSubmitLoading || isPreviewLoading}
          >
            Reset Ruleset
          </SecondaryButton>
          <SecondaryButton
            title="Discard Changes"
            handleOnClick={handleDiscardChanges}
            className="px-2 py-0 text-sm"
            disabled={
              isSubmitLoading ||
              isPreviewLoading ||
              Object.keys(modifiedFields).length === 0
            }
          >
            Discard Changes
          </SecondaryButton>
          <PrimaryButton
            title="Save"
            handleOnClick={handleStartPreview}
            className="px-2 text-sm"
            disabled={
              isSubmitLoading ||
              isPreviewLoading ||
              Object.keys(modifiedFields).length === 0
            }
          >
            Save
          </PrimaryButton>
        </div>
        <div id="rulesets-body" className="flex h-full gap-6">
          <div className="border-soFresh-140 no-scrollbar h-[1000px] w-64 overflow-x-hidden overflow-y-scroll rounded border-r">
            {isLoading && (
              <div className="self-center">
                <Loading
                  loadingMessage="Loading League Tree"
                  messageClassName="text-sm"
                />
              </div>
            )}
            {customRuleset && (
              <LeagueTree
                customRuleset={customRuleset}
                selectedQualifiedPath={selectedQualifiedPath}
                onSelectRuleset={handleSelectRuleset}
                disabled={isPreviewLoading}
              />
            )}
            {gameOpsGlobalRuleset && gameModesGlobalRulesets && (
              <LeagueTree
                globalRuleset={gameOpsGlobalRuleset}
                gameModesGlobalRulesets={gameModesGlobalRulesets}
                selectedQualifiedPath={selectedQualifiedPath}
                onSelectRuleset={handleSelectRuleset}
                disabled={isPreviewLoading}
              />
            )}
            {!isLoading &&
              !gameOpsGlobalRuleset &&
              !gameModesGlobalRulesets &&
              !customRuleset && (
                <div className="text-soClean-100 text-center text-sm">
                  No rulesets available
                </div>
              )}
          </div>
          <div className="flex grow flex-col gap-4">
            {currentRuleset && !isRulesetFetching && (
              <>
                <div className="flex flex-row gap-4">
                  <div className="flex w-1/2 flex-col gap-4">
                    <StateManagement />
                    <PlayerSelectionRules />
                  </div>
                  <LineupLimits />
                </div>
                <div className="flex flex-row gap-4">
                  <Metadata />
                </div>
              </>
            )}
            {isRulesetFetching && (
              <div className="self-center">
                <Loading loadingMessage="Loading ruleset" className="pt-2" />
              </div>
            )}
          </div>
        </div>

        {isSubmitSuccess && (
          <MessageBanner
            code="SUCCESS"
            message="Ruleset updated successfully"
            onCancel={closeMessageBanner}
          />
        )}

        {isSubmitError && (
          <MessageBanner
            code="ERROR"
            message="Error updating ruleset"
            onCancel={closeMessageBanner}
          />
        )}

        <PreviewConfirmationModal
          isOpen={isPreviewModalOpen}
          previewData={previewData}
          handleClosePreviewModal={handleClosePreviewModal}
          handleConfirmSubmitChanges={handleConfirmSubmitChanges}
          currentRuleset={currentRuleset}
          isSubmitLoading={isSubmitLoading}
        />
      </div>
    </RulesetsContext.Provider>
  );
};

export const RulesetsDashboard = withProfiler(RulesetsDashboardView);
